import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { TrimmedString } from "./baseSchemas.js";
import { ProviderInstanceConfig, ProviderInstanceId } from "./providerInstance.js";
// ── Client Settings (local-only) ───────────────────────────────
export const TimestampFormat = Schema.Literals(["locale", "12-hour", "24-hour"]);
export const DEFAULT_TIMESTAMP_FORMAT = "locale";
export const SidebarProjectSortOrder = Schema.Literals(["updated_at", "created_at", "manual"]);
export const DEFAULT_SIDEBAR_PROJECT_SORT_ORDER = "updated_at";
export const SidebarThreadSortOrder = Schema.Literals(["updated_at", "created_at"]);
export const DEFAULT_SIDEBAR_THREAD_SORT_ORDER = "updated_at";
export const MIN_SIDEBAR_THREAD_PREVIEW_COUNT = 1;
export const MAX_SIDEBAR_THREAD_PREVIEW_COUNT = 15;
export const SidebarThreadPreviewCount = Schema.Int.check(Schema.isBetween({
    minimum: MIN_SIDEBAR_THREAD_PREVIEW_COUNT,
    maximum: MAX_SIDEBAR_THREAD_PREVIEW_COUNT,
}));
export const DEFAULT_SIDEBAR_THREAD_PREVIEW_COUNT = 6;
export const ClientSettingsSchema = Schema.Struct({
    autoOpenPlanSidebar: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
    confirmThreadArchive: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
    confirmThreadDelete: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
    sidebarProjectSortOrder: SidebarProjectSortOrder.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_SORT_ORDER))),
    sidebarThreadSortOrder: SidebarThreadSortOrder.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_SORT_ORDER))),
    sidebarThreadPreviewCount: SidebarThreadPreviewCount.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_PREVIEW_COUNT))),
    sidebarShowOnlyMyProjects: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
    timestampFormat: TimestampFormat.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_TIMESTAMP_FORMAT))),
});
export const DEFAULT_CLIENT_SETTINGS = Schema.decodeSync(ClientSettingsSchema)({});
// ── Server Settings (server-authoritative) ────────────────────
const makeBinaryPathSetting = (fallback) => TrimmedString.pipe(Schema.decodeTo(Schema.String, SchemaTransformation.transformOrFail({
    decode: (value) => Effect.succeed(value || fallback),
    encode: (value) => Effect.succeed(value),
})), Schema.withDecodingDefault(Effect.succeed(fallback)));
/**
 * A Codex provider instance's settings, read from the server's settings file,
 * which no client writes.
 *
 * There is deliberately no home-directory setting. The product materializes an
 * isolated `CODEX_HOME` per instance holding a configuration that names the Dovod
 * inference proxy as the only model provider; a home anyone else supplies would
 * be a path straight to a third-party provider, bypassing the proxy and its
 * metering.
 */
export const CodexSettings = Schema.Struct({
    enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
    binaryPath: makeBinaryPathSetting("codex"),
    customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export const ClaudeSettings = Schema.Struct({
    enabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
    binaryPath: makeBinaryPathSetting("claude"),
    homePath: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
    customModels: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
    launchArgs: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
});
export const ObservabilitySettings = Schema.Struct({
    otlpTracesUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
    otlpMetricsUrl: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
});
export const ServerSettings = Schema.Struct({
    // Legacy single-instance-per-driver settings. Continues to be the source
    // of truth until `providerInstances` (below) lands per-driver migration
    // shims and the server starts hydrating instances from it. Driver-specific
    // schemas live here for the duration of the migration; once each driver
    // owns its config in its own package, this struct shrinks to nothing and
    // is removed entirely.
    providers: Schema.Struct({
        codex: CodexSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
        claudeAgent: ClaudeSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    }).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    // New driver-agnostic instance map. Keyed by `ProviderInstanceId`; values
    // are `ProviderInstanceConfig` envelopes. The driver-specific config blob
    // is `Schema.Unknown` at this layer so envelopes with unknown drivers
    // (forks, downgrades, in-flight PR branches) round-trip without loss.
    // See providerInstance.ts for the forward/backward compatibility invariant.
    providerInstances: Schema.Record(ProviderInstanceId, ProviderInstanceConfig).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
    observability: ObservabilitySettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
});
export const DEFAULT_SERVER_SETTINGS = Schema.decodeSync(ServerSettings)({});
export class ServerSettingsError extends Schema.TaggedError()("ServerSettingsError", {
    settingsPath: Schema.String,
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect()),
}) {
    get message() {
        return `Server settings error at ${this.settingsPath}: ${this.detail}`;
    }
}
export const ClientSettingsPatch = Schema.Struct({
    autoOpenPlanSidebar: Schema.optionalKey(Schema.Boolean),
    confirmThreadArchive: Schema.optionalKey(Schema.Boolean),
    confirmThreadDelete: Schema.optionalKey(Schema.Boolean),
    sidebarProjectSortOrder: Schema.optionalKey(SidebarProjectSortOrder),
    sidebarThreadSortOrder: Schema.optionalKey(SidebarThreadSortOrder),
    sidebarThreadPreviewCount: Schema.optionalKey(SidebarThreadPreviewCount),
    sidebarShowOnlyMyProjects: Schema.optionalKey(Schema.Boolean),
    timestampFormat: Schema.optionalKey(TimestampFormat),
});
